"use client";

import React from "react";
import { Check, Loader, Layers3 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:focus-visible:bg-transparent";
const textareaClasses = "flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:focus-visible:bg-transparent";
const benefits = ["See how EloRAG turns approved company knowledge into citation-based answers", "Understand approved-only retrieval, refusal logic, and governance controls", "Review BYO-Key pricing and predictable scaling for real deployments", "Discuss fit for teams, multi-tenant setups, or agency delivery models"];
const useCaseOptions = ["Customer Support", "Internal Knowledge Base", "Compliance & Governance", "Agency / White-Label Delivery", "Other"];
function DemoRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-border bg-muted/50 p-8 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Check className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Request received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="Jane Doe" />
              </FormControl>
              <FormDescription className="text-xs">
                Enter the primary contact for the demo.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" className={inputClasses} placeholder="jane@example.com" />
              </FormControl>
              <FormDescription className="text-xs">
                We will use this to follow up and schedule the walkthrough.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="company" rules={{
        required: "Company / Organization is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="Acme Corp" />
              </FormControl>
              <FormDescription className="text-xs">
                The organization evaluating EloRAG.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="job_title" render={({
        field
      }) => <FormItem>
              <FormLabel>Job Title / Role</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="Head of Knowledge Management" />
              </FormControl>
              <FormDescription className="text-xs">
                Helps us tailor the discussion.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" className={inputClasses} placeholder="+1 (555) 123-4567" />
              </FormControl>
              <FormDescription className="text-xs">
                Optional, for quicker coordination.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_demo_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Demo Date</FormLabel>
              <FormControl>
                <input {...field} type="date" className={inputClasses} />
              </FormControl>
              <FormDescription className="text-xs">
                Optional, if you already have a preferred date.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="use_case" rules={{
      required: "Please select a primary use case"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Primary Use Case</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a use case" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {useCaseOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormDescription className="text-xs">
              Select the main scenario you want to explore.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details</FormLabel>
            <FormControl>
              <textarea {...field} className={textareaClasses} placeholder="Tell us about your use case, environment, or any questions you want covered." />
            </FormControl>
            <FormDescription className="text-xs">
              Extra context helps us prepare a more relevant walkthrough.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Submitting request...
          </> : "Request Demo"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </Form>;
}
export default function BookYourDemo() {
  return <section id="book-your-demo" className="bg-background py-14 lg:py-18">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col justify-center">
            <div className="mb-8">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                <Layers3 className="size-4" />
                Book a demo
              </div>

              <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-[3.45rem] leading-[1.05]">
                See how EloRAG works in a real deployment
              </h1>

              <p className="mt-6 text-lg leading-8 text-muted-foreground">
                Get a live walkthrough of how EloRAG helps teams and agencies deploy controlled RAG
                without building the full governance and retrieval layer themselves.
              </p>
            </div>

            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground">
                What to expect on your demo
              </h3>

              <ul className="space-y-4">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Check className="size-3.5 text-primary" />
                    </div>
                    <span className="text-base text-muted-foreground">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-10 rounded-xl border border-border bg-muted/30 p-6">
              <p className="text-sm font-medium text-foreground">
                “EloRAG gives us a deployable RAG layer with stronger control, clearer retrieval boundaries, and answers we can actually verify.”
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                  JD
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Jane Doe</p>
                  <p className="text-xs text-muted-foreground">Head of Knowledge Management</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-b from-border to-transparent opacity-50 blur-sm" aria-hidden="true" />

            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-10">
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-card-foreground">
                  Book a Demo Request
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Fill out the form below and our team will contact you to schedule a tailored walkthrough.
                </p>
              </div>

              <FormProvider formId="demo_request_form_book_a_demo_contact_sales" sectionName="Book Your Demo" defaultValues={{
              full_name: "",
              email: "",
              company: "",
              job_title: "",
              phone: "",
              preferred_demo_date: "",
              use_case: "",
              message: ""
            }} submitText="Request Demo" successMessage="Thank you for your interest. Our team will contact you shortly to schedule your demo.">
                <DemoRequestForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}